// Unified storage utilities for localStorage and Telegram CloudStorage
// - JSON encode/decode with backward compatibility for plain strings
// - Size validation for Telegram CloudStorage (64KB per key)
// - Minimal pub/sub to integrate with useSyncExternalStore

import { WebApp } from "@/shared/telegram"

type Listener = () => void

// Simple in-memory pub/sub per key
const listenersMap = new Map<string, Set<Listener>>()
const bootstrapListenersMap = new Map<string, Set<Listener>>()

const notify = (map: Map<string, Set<Listener>>, key: string) => {
  const set = map.get(key)
  if (!set) return
  for (const l of Array.from(set)) {
    try {
      l()
    } catch {
      // no-op
    }
  }
}

export interface StorageAdapter {
  getItem<T>(key: string): Promise<T | null>
  setItem<T>(key: string, value: T): Promise<void>
  removeItem(key: string): Promise<void>
}

const textEncoder = new TextEncoder()
const MAX_CLOUD_VALUE_BYTES = 64 * 1024 // 64KB per key

const safeParse = <T>(raw: string | null | undefined): T | null => {
  if (raw == null) return null
  // Try JSON.parse, fallback to raw string cast
  try {
    return JSON.parse(raw) as T
  } catch {
    return raw as unknown as T
  }
}

export const localStorageAdapter: StorageAdapter = {
  async getItem<T>(key: string): Promise<T | null> {
    try {
      const raw = localStorage.getItem(key)
      return safeParse<T>(raw)
    } catch {
      return null
    }
  },
  async setItem<T>(key: string, value: T): Promise<void> {
    try {
      const raw = JSON.stringify(value)
      localStorage.setItem(key, raw)
    } catch {
      // ignore
    }
  },
  async removeItem(key: string): Promise<void> {
    try {
      localStorage.removeItem(key)
    } catch {
      // ignore
    }
  }
}

export const cloudStorageAdapter: StorageAdapter = {
  async getItem<T>(key: string): Promise<T | null> {
    return new Promise<T | null>(resolve => {
      try {
        WebApp.CloudStorage.getItem(key, (err, value) => {
          if (err) {
            resolve(null)
            return
          }
          resolve(safeParse<T>(value))
        })
      } catch {
        resolve(null)
      }
    })
  },
  async setItem<T>(key: string, value: T): Promise<void> {
    return new Promise<void>(resolve => {
      try {
        const raw = JSON.stringify(value)
        // Validate size limit per Telegram docs (64KB per key)
        const size = textEncoder.encode(raw).byteLength
        if (size > MAX_CLOUD_VALUE_BYTES) {
          // Too large for CloudStorage, skip cloud write
          console.warn(
            `[cloudStorageAdapter] Value for key "${key}" exceeds 64KB (${size} bytes); skipping cloud write.`
          )
          resolve()
          return
        }
        WebApp.CloudStorage.setItem(key, raw, () => {
          resolve()
        })
      } catch {
        resolve()
      }
    })
  },
  async removeItem(key: string): Promise<void> {
    return new Promise<void>(resolve => {
      try {
        WebApp.CloudStorage.removeItem(key, () => {
          resolve()
        })
      } catch {
        resolve()
      }
    })
  }
}

export type SyncedPref<T> = {
  // React useSyncExternalStore plumbing
  subscribe(listener: Listener): () => void
  getSnapshot(): T | null
  subscribeBootstrap(listener: Listener): () => void
  getBootstrapSnapshot(): boolean
  // Mutators
  set(value: T): Promise<void>
  clear(): Promise<void>
}

export function createSyncedPref<T>(
  key: string,
  options?: {
    local?: StorageAdapter
    cloud?: StorageAdapter
    defaultValue?: T | null
    // Optional validator for values
    validate?: (v: unknown) => v is T
  }
): SyncedPref<T> {
  const local = options?.local ?? localStorageAdapter
  const cloud = options?.cloud ?? cloudStorageAdapter

  // Initialize from local quickly to reduce flicker
  let current: T | null = null
  let isBootstrapped = false

  const ensureListeners = () => {
    if (!listenersMap.has(key)) listenersMap.set(key, new Set())
    return listenersMap.get(key) as Set<Listener>
  }

  const ensureBootstrapListeners = () => {
    if (!bootstrapListenersMap.has(key)) bootstrapListenersMap.set(key, new Set())
    return bootstrapListenersMap.get(key) as Set<Listener>
  }

  const setCurrent = (next: T | null) => {
    current = next
    notify(listenersMap, key)
  }

  const markBootstrapped = () => {
    if (isBootstrapped) return
    isBootstrapped = true
    notify(bootstrapListenersMap, key)
  }

  // Load local synchronously (wrapped in async adapter), then fetch cloud and update if different
  // Kick off bootstrapping immediately
  void (async () => {
    const localVal = await local.getItem<T>(key)
    if (localVal != null && (!options?.validate || options.validate(localVal))) {
      current = localVal
      notify(listenersMap, key)
    } else if (options?.defaultValue !== undefined) {
      current = options.defaultValue
      notify(listenersMap, key)
    }

    try {
      // Cloud takes precedence when present
      const cloudVal = await cloud.getItem<T>(key)
      if (cloudVal != null && (!options?.validate || options.validate(cloudVal))) {
        if (JSON.stringify(cloudVal) !== JSON.stringify(current)) {
          setCurrent(cloudVal)
          // Keep local in sync with cloud
          await local.setItem<T>(key, cloudVal)
        }
      }
    } finally {
      markBootstrapped()
    }
  })()

  return {
    subscribe(listener: Listener) {
      const set = ensureListeners()
      set.add(listener)
      return () => set.delete(listener)
    },
    getSnapshot() {
      return current
    },
    subscribeBootstrap(listener: Listener) {
      const set = ensureBootstrapListeners()
      set.add(listener)
      return () => set.delete(listener)
    },
    getBootstrapSnapshot() {
      return isBootstrapped
    },
    async set(value: T) {
      if (options?.validate && !options.validate(value)) return
      await local.setItem<T>(key, value)
      // Fire before awaiting cloud to keep UI responsive
      setCurrent(value)
      await cloud.setItem<T>(key, value)
    },
    async clear() {
      await local.removeItem(key)
      setCurrent(null)
      await cloud.removeItem(key)
    }
  }
}
